import {
  isCommercialEuChannelPartner,
  loginHasDirectBusiness,
  loginIsCommercialEuChannelPartner,
} from "@/utils/directBusiness";
import { useEffect, useMemo, useState } from "react";
import organizationClient from "@apis/organizationClient";
import useAvailibleMenu from "./useAvailibleMenu";
import { OWN_LEVEL_DETAIL_ROUTES } from "@/contexts/OrgContext";
import {
  getEnhancedOrgType,
  MenuSourceConst,
  MenuTypeConst,
  OrgTypeConst,
  type MenuType,
  type MenuRoute,
  type AvailibleMenuData,
} from "./menuTypes";
import type { LoginInfo, LoginOrganisationInfo, Partner } from "@/types/LoginInfo";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { hasFeature } from "@hooks/useFeatures";
import { UserPermission } from "@/types/generated/UserPermission";
import { hasPermission } from "@hooks/usePermissions";

interface ExtendedOrganisationInfo extends LoginOrganisationInfo {
  partner?: Partner;
}

interface ExtendedMenuRoute extends MenuRoute {
  category?: string;
  type?: string;
  reportType?: string;
  param?: string;
  title?: string;
  views?: ExtendedMenuRoute[];
}

/**
 * Return type of useCrossLevelMenu hook
 */
interface CrossLevelMenuData extends AvailibleMenuData {
  type: MenuType;
  loading: boolean;
  routes: MenuRoute[];
  /**
   * Every org-scoped path this menu can serve, listed regardless of whether an
   * org is currently sliced. `allPaths` only ever holds the ACTIVE level, so it
   * can't answer "is /trackers one of ours?" while browsing cross-org —
   * which SidebarBody needs in order to default `?org=` on a deep link that
   * arrives without one.
   */
  orgScopedPaths: Record<string, true>;
  /**
   * Both level paths of every entry, keyed by EITHER of them, so a page can be
   * translated to its counterpart when the org slice changes — `/trackers` at
   * cross-org level is `/trackers` once an org is picked. Entries with no
   * cross-org page map to themselves on both sides.
   */
  levelIndex: Record<string, LevelPair>;
}

/** One side of a two-level menu entry. */
interface LevelPath {
  to: string;
  subPages?: string[];
}

/** The same concept at both levels. */
export interface LevelPair {
  org: string;
  cross: string;
}

/**
 * BI dashboard management — the Insights entry spreads this in. One path for
 * both levels: the page reads `?org=` (see BiDashBoardsPage).
 */
const BI_DASHBOARDS: LevelPath = {
  to: "/bi_dashboards",
  subPages: ["/bi_dashboard_manage"],
};

/**
 * Concepts whose *page* differs between the two levels.
 *
 * Every other entry has a single path serving both — the page reads `?org=` and
 * scopes itself. A split concept instead appears as **two ordinary menu
 * entries** whose `view` names the level it belongs to; this table only records
 * that the two paths are the same concept, which is what lets `levelIndex`
 * translate the page you are on when the level changes.
 *
 * Empty today. Quotes left in STIC-15790: its cross-org page
 * (`/quotes_storefront`, Sensolus' internal tooling) left the cross-level menu,
 * so the partner page `/quotes_storefront_partner` self-pairs like any other
 * single-path entry. Device returns left in STIC-16159: `/rma_declaration`
 * turned out to be Sensolus' own manage list rather than an "All" view of
 * `/rma_declaration_view`, and moved to own-level Operations team; the
 * read-only page is own-level only since.
 */
const LEVEL_SPLITS: { org: string; cross: string }[] = [];

/**
 * Derives the two bookkeeping maps SidebarBody needs, by walking the finished
 * route tree. Neither has anything to do with visibility — that is each entry's
 * own `view` / `disabled`.
 *
 * - `orgScopedPaths` — the org-scoped path of every entry, so a deep link that
 *   arrives without `?org=` can be defaulted to one.
 * - `levelIndex` — each path mapped to its (org, cross) pair, so switching level
 *   can translate the page you are on. Identical on both sides except for
 *   {@link LEVEL_SPLITS}.
 */
const collectLevelPaths = (routes: ExtendedMenuRoute[]) => {
  const orgScopedPaths: Record<string, true> = {};
  const levelIndex: Record<string, LevelPair> = {};

  // Takes `to` separately so a narrowed `route.to` can be passed straight in —
  // MenuRoute.to is optional (section headers have none) and the narrowing does
  // not survive being handed on as an object.
  const register = (to: string, subPages?: string[]) => {
    orgScopedPaths[to] = true;
    subPages?.forEach((sub) => {
      orgScopedPaths[sub] = true;
    });
    levelIndex[to] = { org: to, cross: to };
  };

  const visit = (list: ExtendedMenuRoute[]) => {
    list.forEach((route) => {
      if (route.views) visit(route.views);
      if (route.to) register(route.to, route.subPages);
    });
  };
  visit(routes);

  // Both halves of a split concept point at the same pair, overwriting the
  // self-pairing the walk gave them.
  LEVEL_SPLITS.forEach((pair) => {
    levelIndex[pair.org] = pair;
    levelIndex[pair.cross] = pair;
  });

  return { orgScopedPaths, levelIndex };
};

/**
 * Hook for the cross-level menu — the admin menu used to browse organisation
 * data, either across every visible org or narrowed to one.
 *
 * Behaviour is driven entirely by whether an org is sliced:
 * - no `?org=`  → cross-org pages (the former "All" menu)
 * - `?org=<id>` → the org-scoped pages (the former selected-org menu)
 *
 * Artifacts an organisation owns *itself* (System's organisations, partners,
 * price lists, Salesforce; a partner's own tags and filters) are not here —
 * they live in useOwnLevelMenu, behind the "System" pill.
 *
 * @param loginInfo - Current user's login information including features and permissions
 * @param orgId - Organisation sliced via `?org=`; undefined/0 means "all organisations"
 * @returns Menu data including routes, available paths, and loading state
 */
const useCrossLevelMenu = (loginInfo: LoginInfo, orgId?: number): CrossLevelMenuData => {
  const [orgSelected, setOrgSelected] = useState<ExtendedOrganisationInfo | null>(null);
  // The org id `orgSelected` has finished resolving for (set even when the fetch
  // fails), so the "menu caught up to the URL org" check below can't get stuck
  // loading if organizationClient.findOne rejects.
  const [resolvedOrgId, setResolvedOrgId] = useState<number | null>(null);

  // Path flipping keys off the URL, not off the fetched org: a failed org fetch
  // must not silently bounce every entry back to its cross-org page.
  const hasOrg = orgId != null && orgId !== 0;

  // The two axes the entries below declare their visibility against (STIC-15615).
  // See OrgTypeConst in menuTypes for the contract and the spreadsheet it
  // implements; the level axis is implicit — being in this hook *is* cross level.
  //
  // `isSystemLogin` only matters where the sheet gives partners a different cell
  // from System (Geolayers is the sole such row); everything else keys purely off
  // which org is sliced.
  const isSystemLogin = getEnhancedOrgType(loginInfo) === OrgTypeConst.SYSTEM;

  // What the menu is pointed at: the sliced org's type, or ALL when none is.
  // A PARTNER or MULTI_ORG login can never slice a partner org other than its
  // own — that is the own level — so in practice they only ever see NORMAL/ALL.
  //
  // Reads the fetched org, since only the fetch knows its type. While that is in
  // flight this is null and the greyed-out branches win, which is harmless:
  // `orgNotReady` below makes the hook report `loading` until it catches up.
  const slicedOrgType = hasOrg ? orgSelected?.organisationType ?? null : "ALL";

  // The routes, plus the two path maps derived from them.
  //
  // MUST be memoised. SidebarBody keys effects on this menu's identity, and one
  // of them calls setSelectedMenu; handing back a fresh object every render
  // re-fires it in a loop ("Maximum update depth exceeded").
  const { initialRoutes, orgScopedPaths, levelIndex } = useMemo(() => {
    // Entitlement half of the Quotes rule. STIC-16004: browsing a partner org,
    // that partner must be a commercial channel partner on the EU price list
    // (marks 1, 2, 3 of the Partner Variant Access Matrix, shared with
    // quoteAccess.ts); browsing a customer org, the login itself must qualify,
    // which the view below tests; at "All" the storefront asks which organisation
    // to quote for, so it stays open.
    const canManageQuotes =
      hasFeature(FeatureKey.quote_manage) &&
      hasPermission(UserPermission.MENU_ADMIN_QUOTES) &&
      (!hasOrg ||
        !orgSelected ||
        "PARTNER" === orgSelected.organisationType ||
        "NORMAL" === orgSelected.organisationType);
    // A partner that is not a commercial channel partner on the EU price list
    // keeps the entry but greyed ("grey for context"): the storefront explains
    // why. Menu directory, 2026-09-19.
    const quotesPartnerNotEligible =
      hasOrg &&
      !!orgSelected &&
      "PARTNER" === orgSelected.organisationType &&
      !isCommercialEuChannelPartner(orgSelected);

    const built: ExtendedMenuRoute[] = [
    {
      collapse: false,
      // System-only internal page (STIC-15790): never shown to partner or
      // normal logins. Meaningless without an org, so greyed at "All" but kept
      // visible so the pill row doesn't jump around when you slice an org.
      // Deliberately NOT the landing page — that stays the tracker list.
      view: isSystemLogin && hasPermission(UserPermission.ORGANIZATION_VIEW),
      disabled: !hasOrg,
      name:
        orgSelected?.organisationType === "PARTNER"
          ? translate(LanguageKey.partner_dashboard_key)
          : translate(LanguageKey.organization_dashboard_key),
      state: "orgDetailDashboard",
      layout: "",
      icon: <span className="dashboard-menu-icon menu-icon"></span>,
      views: [],
      topOverview: true,
      to: "/organisation_dashboard",
    },
    // Presales and Sensolus support (STIC-15790): the cross-level twins of the
    // own-level groups - the business between an organisation and Sensolus.
    // Demos and the Battery calculator are own-level only.
    //
    // Four own-level items have no entry here. Demos, because the organisation
    // register is own-level by definition. Order and Support tickets, because
    // they cannot be sliced per organisation in practice: ZendeskTicket carries
    // an ownedByOrg, and the repository defines the filter, but 99% of the rows
    // have none (5433 of 5475 orders, 19970 of 19996 support), so slicing any
    // real organisation returned an empty list. They stay own-level only.
    // Device returns (STIC-16159), because both positions were redundant: the
    // "All" half was Sensolus' own manage list, now at own-level Operations
    // team, and a sliced organisation's returns are already in the partner's
    // own-level list (whole tree, STIC-15976) and in System's manage list.
    {
      collapse: true,
      name: translate(LanguageKey.presales_key),
      state: "openPresales",
      views: [
        {
          // A commercial channel partner quotes FOR its customers, which is what the
          // partner storefront is for, so it needs this entry while browsing one of
          // them. STIC-15790 lists Quotes at own AND cross level, but the cross entry
          // shipped System-only and dropped the path a partner has on 6.1.x - the
          // route condition (PARTNER_QUOTES_ACCESS) allowed it all along, only the
          // menu hid it.
          view: (isSystemLogin || loginIsCommercialEuChannelPartner(loginInfo)) && canManageQuotes,
          disabled: quotesPartnerNotEligible,
          isForceAvailableMenu: true,
          layout: "",
          name: translate(LanguageKey.quotes_key),
          to: "/quotes_storefront_partner",
          subPages: ["/quotes_partner", "/quote_detail_partner"],
        },
        {
          // Presales, after Quotes, matching the own-level order. A demo environment belongs to System or to a partner, never
          // to a customer org — hence greyed while a NORMAL org is sliced.
          view: isSystemLogin && hasPermission(UserPermission.MENU_ADMIN_DEMO),
          disabled: slicedOrgType === "NORMAL",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_DEMO_description_key),
          to: "/org_demo",
          subPages: ["/org_demo_manage"],
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.sensolus_support_group_key),
      state: "openSensolusSupport",
      views: [
        {
          // Row 17. The organisation's tickets with Sensolus, which System reads
          // from the cross level for any org and at "All". The zendesk_ticket
          // feature key belongs to the organisation being browsed, not to the
          // System login browsing it, so it is not tested here — same reasoning
          // as /partner_tag and /manage_organisation_filters.
          view: isSystemLogin && hasPermission(UserPermission.MENU_ADMIN_ZENDESK_SUPPORT_TICKET),
          disabled: false,
          layout: "",
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_ZENDESK_SUPPORT_TICKET_description_key
          ),
          to: "/support_tickets",
        },
        {
          view:
            (isSystemLogin || loginHasDirectBusiness(loginInfo)) &&
            hasPermission(UserPermission.MENU_ADMIN_BILLING),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          isForceAvailableMenu: true,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_BILLING_description_key),
          to: "/invoicing",
          subPages: ["/invoice_detail", "/quotation_detail"],
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.trackers_and_sensors_key),
      state: "openTrackersSensors",
      views: [
        // Same order as the own-level menu (STIC-15790), so switching level
        // does not reshuffle the section.
        {
          view: hasPermission(UserPermission.MENU_ADMIN_TRACKERS),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_TRACKERS_description_key),
          to: "/trackers",
          subPages: ["/trackers_admin_update", "/import_tracker_settings", "/import_tracker_settings_admin"],
        },
        {
          view: hasPermission(UserPermission.MENU_ADMIN_BLE_SENSOR),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_BLE_SENSOR_description_key),
          to: "/ble_sensor_list",
          subPages: ["/ble_sensor_update", "/ble_sensor_import"],
        },
        {
          // Row 28: System keeps the cross level — greyed on a sliced NORMAL org,
          // which can never be granted the usage_profile feature, usable on a
          // partner and at "All". A partner login gets no cross entry at all: the
          // sheet hides it, because the only profiles it could reach are its own,
          // and those live at its own level ("Only limited amount of partner orgs
          // can create own profiles").
          view: isSystemLogin && hasPermission(UserPermission.MENU_ADMIN_USAGE_PROFILE),
          disabled: slicedOrgType === "NORMAL",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_USAGE_PROFILE_description_key),
          to: "/device_usage_profile",
          // Cross level only -- the own menu declares the twin without
          // `_admin`, and declaring it here too would stop it being
          // own-exclusive, which is the whole mechanism (see
          // OWN_LEVEL_DETAIL_ROUTES).
          subPages: [
            OWN_LEVEL_DETAIL_ROUTES["/device_usage_profile"].cross,
            "/device_usage_profile_compare_admin",
          ],
        },
        {
          // Diagnostics only mean anything for one org, so "All" is greyed and
          // the page renders NotAvailableAtThisLevel.
          view:
            hasPermission(UserPermission.MENU_ADMIN_DIAGNOSTIC),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_DIAGNOSTIC_description_key),
          to: "/diagnostic_organisation",
        },
        {
          // Own level only (STIC-15790) — factory uploads are done against the
          // organisation you are logged into, never against a browsed one. The
          // entry stays registered so the path keeps resolving to this menu.
          view: false,
          disabled: false,
          layout: "",
          name: translate(LanguageKey.sidebar_factory_upload_key),
          to: "/factory_upload",
          subPages: ["/factory_upload_update", "/factory_order_create"],
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_BULK_OPERATION),
          // Row 27: usable at "All" as well, so the page has to hold up with no
          // organisation selected.
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_BULK_OPERATION_description_key),
          to: "/bulk_manage",
          subPages: [
            "/bulk_operations",
            "/bulk_operations_admin",
            "/bulk_results_detail",
            "/bulk_results_detail_system",
          ],
        },
        {
          view: hasPermission(UserPermission.MENU_ADMIN_EOL_TRACKERS),
          // Enabled at "All" too (STIC-15790): both the backend and the page
          // handle a cross-org list — the page sends no org filter when none is
          // sliced, so it simply lists every org's end-of-life devices.
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_EOL_TRACKERS_description_key),
          to: "/end_of_life",
        },
      ],
    },
    // Reports group (STIC-15790). Report list and Report schedules deliberately
    // duplicate the home-menu pages on admin-side routes (/reports_admin,
    // /report_schedule_admin): the home menu keeps /reports and
    // /report_schedule, and sharing those paths made HOME win the menu
    // resolution over this menu (the cross level was unreachable for partners
    // on those two pages).
    {
      collapse: true,
      name: translate(LanguageKey.reports_key),
      state: "openReports",
      views: [
        {
          view:
            hasPermission(UserPermission.MENU_HOME_REPORT_LIST),
          to: "/reports_admin",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_REPORT_LIST_description_key),
          subPages: ["/create_report"],
        },
        {
          view:
            hasPermission(UserPermission.MENU_HOME_REPORT_SCHEDULES),
          to: "/report_schedule_admin",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_HOME_REPORT_SCHEDULES_description_key),
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_REPORT_TEMPLATE),
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_REPORT_TEMPLATE_description_key),
          to: "/report_template_library",
          subPages: ["/report_template_library_manage"],
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.location_key),
      state: "openLocation",
      views: [
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_GEOZONES),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_GEOZONES_description_key),
          to: "/areas",
          // Cross level only -- the own menu declares the twin without
          // `_admin`, and declaring it here too would stop it being
          // own-exclusive, which is the whole mechanism (see
          // OWN_LEVEL_DETAIL_ROUTES).
          subPages: [OWN_LEVEL_DETAIL_ROUTES["/areas"].cross, "/import_export_geozones"],
        },
        {
          // Row 42: on offer at every cross column, greyed at "All" — a geolayer
          // list is one organisation's. `hasOrg || isSystemLogin` hid it from a
          // partner at "All" instead of greying it.
          view: hasPermission(UserPermission.MENU_ADMIN_GEOLAYER),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_GEOLAYER_description_key),
          // System sees a greyed "All"; for a partner login "All" is hidden outright.
          to: "/geolayers",
          subPages: ["/geolayer_update"],
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_SITE_MAP),
          // STIC-16142: a site map can only be owned by a normal organisation. Greyed
          // at All (no org sliced) and when System slices a partner organisation;
          // the page then explains that the object only exists in normal
          // organisations.
          disabled: slicedOrgType !== "NORMAL",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_SITE_MAP_description_key),
          to: "/site_maps",
          subPages: ["/site_map_update"],
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_INFRASTRUCTURE),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_INFRASTRUCTURE_description_key),
          to: "/infrastructures",
          subPages: [
            "/infrastructures_update",
            "/import_infrastructure_settings",
            "/import_infrastructure_settings_admin",
          ],
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_WIFI),
          // Row 45: only a customer organisation has private wifi points, so a
          // sliced PARTNER is greyed too, not just "All".
          disabled: slicedOrgType !== "NORMAL",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_WIFI_description_key),
          to: "/wifi",
          subPages: ["/wifi/blocked", "/wifi/unresolved"],
        },
        {
          // Row 46: every login type, greyed at "All" — the adjustments belong to
          // one organisation.
          view: hasPermission(UserPermission.MENU_ADMIN_PRIVATE_CELLULAR),
          disabled: !hasOrg,
          layout: "",
          name: translate(LanguageKey.cellular_tower_adjustments_key),
          to: "/private_cellular",
        },
        {
          // Settings configure ONE normal org and never inherit downwards, so a
          // partner org or "All" shows the item greyed.
          view:
            hasPermission(UserPermission.MENU_ADMIN_LOCATION_SETTINGS) ||
            hasPermission(UserPermission.GEOZONE_BEHAVIOR_VIEW) ||
            hasPermission(UserPermission.JOURNEY_ALGO_SETTINGS_VIEW),
          // Only meaningful for a customer organisation.
          disabled: slicedOrgType !== "NORMAL",
          param: hasPermission(UserPermission.BLE_TAG_TRACKING_SETTINGS_MANAGE)
            ? "type=edit"
            : "type=view",
          layout: "",
          name: translate(LanguageKey.location_settings_key),
          to: "/location_settings",
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.types_and_tags_key),
      state: "openTags",
      views: [
        {
          view: hasPermission(UserPermission.MENU_ADMIN_ASSET_TYPES),
          // Only meaningful for a customer organisation.
          disabled: slicedOrgType !== "NORMAL",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_ASSET_TYPES_description_key),
          to: "/asset_types",
        },
        {
          view: hasPermission(UserPermission.MENU_ADMIN_ASSET_TAGS),
          // Only meaningful for a customer organisation.
          disabled: slicedOrgType !== "NORMAL",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_ASSET_TAGS_description_key),
          to: "/asset_tags_manage",
          subPages: ["/update_device_tag"],
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_GEOZONE_TAGS),
          disabled: !hasOrg,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_GEOZONE_TAGS_description_key),
          to: "/geozone_tags_manage",
          subPages: ["/update_geozone_tag"],
        },
        {
          // System reaches a partner's own tags from the cross level
          // (STIC-15790). Only a partner organisation has them, so the item is
          // listed but greyed until one is sliced. A partner login manages its
          // own tags from the own level instead, and row 58 drops the entry there
          // rather than greying it — hence the login-type test.
          view: isSystemLogin && hasPermission(UserPermission.MENU_ADMIN_PARTNER_TAGS),
          disabled: slicedOrgType !== "PARTNER",
          isForceAvailableMenu: true,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_PARTNER_TAGS_description_key),
          to: "/partner_tag",
          subPages: ["/partner_tag_manage"],
        },
      ],
    },
    // AI management — the administration of agents, as opposed to running them.
    // Both entries used to sit under Insights, which is where they landed when
    // there were two of them and no better home; they are not insights, and a
    // group of their own is what lets the pair grow (STIC-16335).
    //
    // "AI management" rather than "AI Agents": the own-level menu already
    // carries an AI Agents group — the favourited agents plus "All agents",
    // filled at runtime by useAgentMenuItems — and two groups of that name in
    // one sidebar is worse than none. The names also say the difference: that
    // group runs agents, this one manages them.
    {
      collapse: true,
      name: translate(LanguageKey.ai_management_key),
      state: "openAiManagement",
      views: [
        {
          view: hasPermission(UserPermission.MENU_ADMIN_AGENT),
          // Available at "All" too: the search already reads no org as "every org
          // this login manages" (applyOrgScope), and the list names the owning
          // organisation per row there.
          disabled: false,
          layout: "",
          name: translate(LanguageKey.agents_activations_title_key),
          // Every page reached from the list, so the entry stays highlighted once
          // you drill into one. Named by prefix — subPage matching is
          // prefix-based, and each route carries an agent name or id.
          //
          // Deliberately not "/active_agents" itself, which would cover all
          // three: SidebarBody skips defaulting ?org= for a path listed here
          // verbatim, so listing the list's own path would break deep links that
          // arrive without an org. That is why each detail page has a fixed
          // segment of its own rather than hanging off the list path directly
          // (STIC-16035).
          to: "/active_agents",
          subPages: ["/active_agents/open", "/active_agents/schedules"],
        },
        {
          view: hasPermission(UserPermission.MENU_ADMIN_AGENT),
          // Unlike the activations list, this one has an answer at "All": every
          // agent the manager holds, with its owner on each row.
          disabled: false,
          layout: "",
          name: translate(LanguageKey.agents_store_title_key),
          to: "/agent_store",
          // The management page, which used to hang off the activations list as
          // /enrolled_agents/registered. Declaring it here is what lets it be a
          // top-level path at all: SidebarBody resolves the menu by prefix and
          // falls back to the home menu when nothing claims it, which is how the
          // earlier attempt at a top-level path went wrong.
          subPages: ["/agent_store_update_admin"],
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.insights_key),
      state: "openInsights",
      views: [
        {
          // BI dashboard management (STIC-15790): moved here from the Dashboards
          // group, which keeps the per-dashboard links and "All dashboards".
          view: hasFeature(FeatureKey.dashboard) && hasPermission(UserPermission.MENU_ADMIN_DASHBOARD),
          // Row 62: the cross-org list is on offer at "All" for every login type.
          disabled: false,
          layout: "",
          type: "bi",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_DASHBOARD_description_key),
          category: "dashboard",
          ...BI_DASHBOARDS,
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_ALERT_RULE),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          isForceAvailableMenu: true,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_ALERT_RULE_description_key),
          to: "/manage_alerts",
          subPages: ["/manage_alerts_update"],
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_DEVICE_UTILIZATION_SCHEME),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          layout: "",
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_DEVICE_UTILIZATION_SCHEME_description_key
          ),
          to: "/utilization_scheme",
          subPages: ["/utilization_scheme_update"],
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_FLOW),
          // Only meaningful for a customer organisation.
          disabled: slicedOrgType !== "NORMAL",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_FLOW_description_key),
          to: "/flow_definition",
          subPages: ["/flow_settings"],
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_PROCESS_DEFINITION),
          // Only meaningful for a customer organisation.
          disabled: slicedOrgType !== "NORMAL",
          isForceAvailableMenu: true,
          layout: "",
          title: translate(
            LanguageKey.UserPermission_MENU_ADMIN_PROCESS_DEFINITION_description_key
          ),
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_PROCESS_DEFINITION_description_key
          ),
          to: "/process_definition",
          subPages: ["/process_definition_update"],
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_MAINTENANCE),
          // Only meaningful for a customer organisation.
          disabled: slicedOrgType !== "NORMAL",
          isForceAvailableMenu: true,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_MAINTENANCE_description_key),
          to: "/maintenance_programs",
          subPages: ["/maintenance_programs_manage", "/maintenance_programs_bulk_start"],
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.access_management_key),
      state: "openAccess",
      views: [
        {
          view: hasPermission(UserPermission.MENU_ADMIN_USERS),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_USERS_description_key),
          to: "/users",
          subPages: ["/users_update", "/users_admin_update"],
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_USER_ROLE),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_USER_ROLE_description_key),
          to: "/role_manager",
          // Cross level only -- see useOwnLevelMenu. Declaring the own route here
          // too would stop it being own-exclusive, which is the whole mechanism.
          subPages: [OWN_LEVEL_DETAIL_ROUTES["/role_manager"].cross],
        },
        {
          // System browsing a partner: organisation visibility filters belong to
          // a partner organisation, so the item is greyed off "All" and off a
          // customer org (STIC-15790). Hidden outright for a partner login, which
          // manages its own filters from the own level instead.
          //
          // Permission only, no feature key: the organisation_visibility_filters
          // feature belongs to the partner being browsed, not to the System login
          // doing the browsing - the same reason /partners_details and
          // /partner_tag gate on permission alone here.
          view: isSystemLogin && hasPermission(UserPermission.MENU_ADMIN_ORGANISATION_VISIBILITY_FILTER),
          disabled: slicedOrgType !== "PARTNER",
          isForceAvailableMenu: true,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_ORGANISATION_VISIBILITY_FILTER_description_key),
          to: "/manage_organisation_filters",
          subPages: [
            "/manage_organisation_filters_update",
            "/manage_partner_organisation_filters_update",
          ],
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_VISIBILITY_FILTER),
          // Only meaningful for a customer organisation.
          disabled: slicedOrgType !== "NORMAL",
          layout: "",
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_VISIBILITY_FILTER_description_key
          ),
          to: "/manage_visibility_filters",
          subPages: ["/manage_visibility_filters_update"],
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_AUDIT_LOG),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_AUDIT_LOG_description_key),
          to: "/audit_log",
          subPages: ["/audit_log_view", "/audit_log_aggregated_view"],
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_ORGANIZATION_SSO),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          layout: "",
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_ORGANIZATION_SSO_description_key
          ),
          to: "/single_sign_on",
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.organization_settings_key),
      state: "openOrgSetting",
      views: [
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_ORGANIZATION_DETAILS),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          param: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW) ? "type=edit" : "type=view",
          layout: "",
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_ORGANIZATION_DETAILS_description_key
          ),
          to: "/organization_update",
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_PLATFORM_PLAN),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          param: hasPermission(UserPermission.SYSTEM_ADMIN_VIEW) ? "type=edit" : "type=view",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_PLATFORM_PLAN_description_key),
          to: "/product_plan",
        },
        // Quotes is one of the two concepts with a different page per level, so
        // it is two entries and the level decides which one is visible.
        {
          // Row 91: System reaches a partner's details here; a partner login has
          // no cross-level entry of its own.
          view: isSystemLogin && hasPermission(UserPermission.MENU_ADMIN_PARTNER_DETAIL),
          // Only meaningful for a partner organisation.
          disabled: slicedOrgType !== "PARTNER",
          isForceAvailableMenu: true,
          param: "type=view&id=&tabActive=general_tab",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_PARTNER_DETAIL_description_key),
          to: "/partners_details",
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.customization_key),
      state: "opencustomization",
      views: [
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_IMAGE_LIBRARY),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_IMAGE_LIBRARY_description_key),
          to: "/image_library",
          subPages: ["/image_library_manage"],
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_BUSINESS_CHANNEL) &&
            // MENU_ADMIN_BUSINESS_CHANNEL declares BUSINESS_CHANNEL_VIEW as a
            // dependency, but the role editor only cascades dependencies when a
            // permission is checked, never when one is unchecked. Roles can therefore
            // keep the menu permission while the definition permissions are revoked -
            // check it explicitly here (STIC-15894).
            hasPermission(UserPermission.BUSINESS_CHANNEL_VIEW),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          layout: "",
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_BUSINESS_CHANNEL_description_key
          ),
          to: "/third_party_data",
          subPages: ["/channel_update"],
        },
        {
          // STIC-15910: automation rules only exist on customer organisations, so
          // a partner org and "All" show the item greyed.
          view:
            hasPermission(UserPermission.MENU_ADMIN_AUTOMATION),
          // Row 67: rules live on a customer organisation, so a sliced PARTNER is
          // greyed as well as "All".
          disabled: slicedOrgType !== "NORMAL",
          layout: "",
          name: translate(LanguageKey.automation_rules_key),
          to: "/automation_rule",
          subPages: ["/automation_rule_create", "/automation_rule_update"],
        },
        {
          // Row 97: a partner brands its customers from the cross level too, greyed
          // only at "All".
          view:
            hasPermission(UserPermission.MENU_ADMIN_CUSTOM_INTERFACE),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_CUSTOM_INTERFACE_description_key),
          to: "/custom_branding",
        },
        {
          view: hasPermission(UserPermission.MENU_ADMIN_SUPPORT),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_SUPPORT_description_key),
          to: "/support",
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_LANDING_PAGE),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_LANDING_PAGE_description_key),
          to: "/default_landing_page",
        },
        {
          // STIC-15974: custom_i18n_message left the branding add-on, so 366 orgs lost
          // the entitlement. Without the feature check they would still be offered this
          // page and every call behind it would fail @featurePermission. Sensolus
          // bypasses that check and cannot hold the feature itself (visibleFor is
          // NORMAL/PARTNER), hence the isSystemLogin escape.
          view:
            hasPermission(UserPermission.MENU_ADMIN_CUSTOM_I18N_MESSAGE) &&
            (isSystemLogin || hasFeature(FeatureKey.custom_i18n_message)),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          layout: "",
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_CUSTOM_I18N_MESSAGE_description_key
          ),
          to: "/custom_i18n_messages",
          subPages: ["/custom_i18n_messages_manage"],
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.developers_key),
      state: "openDevelopers",
      views: [
        {
          view: hasPermission(UserPermission.MENU_ADMIN_API_DOCS),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_API_DOCS_description_key),
          to: "/api-docs",
        },
        {
          view: hasPermission(UserPermission.MENU_ADMIN_API_ACCESS),
          // No cross-org page for this concept — greyed until an org is sliced.
          disabled: !hasOrg,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_API_ACCESS_description_key),
          to: "/api-access",
          subPages: ["/api-access-update"],
        },
        {
          view:
            hasPermission(UserPermission.MENU_ADMIN_WEBHOOKS),
          // At "All" only System gets the cross-org list (STIC-15790): it spans
          // every organisation and is told apart by the "Owned by" column
          // (STIC-15806). A partner has no such view, so it stays greyed there.
          // With an org sliced both see that org's webhooks.
          disabled: !hasOrg && !isSystemLogin,
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_WEBHOOKS_description_key),
          to: "/webhooks",
          subPages: ["/callbacks_update"],
        },
      ],
    },
    {
      // CSM team, System only (STIC-15790): the CSM records are about the
      // organisations Sensolus manages, so they are browsed at cross level. A
      // partner never sees this group - identity, not context - so it is hidden
      // rather than greyed.
      collapse: true,
      name: translate(LanguageKey.csm_team_key),
      state: "openCsmTeam",
      views: [
        {
          view: isSystemLogin && hasPermission(UserPermission.MENU_ADMIN_CSM),
          // One organisation's record - greyed until an org is sliced; the
          // page shows the not-available explainer at "All".
          disabled: !hasOrg,
          layout: "",
          name: translate(LanguageKey.csm_info_menu_key),
          to: "/csm_info",
        },
        {
          view: isSystemLogin && hasPermission(UserPermission.MENU_ADMIN_CSM),
          disabled: false,
          layout: "",
          name: translate(LanguageKey.csm_comment_menu_key),
          to: "/csm_comment",
          subPages: ["/csm_comment_create", "/csm_comment_update"],
        },
      ],
    },
    {
      collapse: true,
      name: translate(LanguageKey.engineering_various_key),
      state: "openPlatformNotifications",
      views: [
        {
          // Own-level "System" only. It used to appear at cross-level "All",
          // which the sheet records as a bug.
          view: false,
          disabled: false,
          to: "/notification",
          layout: "",
          name: translate(LanguageKey.UserPermission_MENU_ADMIN_NOTIFICATION_description_key),
          subPages: ["/notification_update"],
        },
        {
          // Own level only — exotic enough that it is hidden here for every
          // org type. The entry stays registered so the path keeps resolving.
          view: false,
          disabled: false,
          layout: "",
          name: translate(
            LanguageKey.UserPermission_MENU_ADMIN_METAR_WEATHER_LOCATION_description_key
          ),
          to: "/metar_weather_location",
          subPages: ["/metar_weather_location_manage"],
        },
      ],
    },
  ];

    return { initialRoutes: built, ...collectLevelPaths(built) };
  }, [hasOrg, slicedOrgType, isSystemLogin, orgSelected, loginInfo]);

  const [routes, setRoutes] = useState<ExtendedMenuRoute[]>(initialRoutes);
  const [data, setData] = useState<CrossLevelMenuData>({
    type: MenuTypeConst.ALL,
    menuSource: MenuSourceConst.CROSS,
    loading: true,
    routes: [],
    availableMenuPaths: {},
    allPaths: {},
    orgScopedPaths: {},
    levelIndex: {},
    // Landing page stays the tracker list in both modes (falls back to the first
    // available item when the user lacks MENU_ADMIN_TRACKERS).
    defaultPath: "/trackers",
  });

  const availibleMenuData = useAvailibleMenu(routes, "/trackers");

  useEffect(() => {
    setData({ ...data, ...availibleMenuData, loading: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availibleMenuData]);

  // Adopt the rebuilt table whenever the sliced org changes — both the `to` flip
  // and most `view` conditions depend on it. Nothing is loaded asynchronously
  // into this menu any more (the Dashboards group moved to own level,
  // STIC-16186 R1), so the whole table is replaced. On mount this is a no-op:
  // `routes` was seeded with this very array, so setRoutes bails.
  useEffect(() => {
    setRoutes((oldRoutes) => (oldRoutes === initialRoutes ? oldRoutes : initialRoutes));
  }, [initialRoutes]);

  useEffect(() => {
    setData((prev) => ({ ...prev, loading: true }));
    if (orgId) {
      if (loginInfo.organisation?.id !== orgId && hasPermission(UserPermission.ORGANIZATION_VIEW)) {
        organizationClient
          .findOne({ id: orgId })
          .then(({ data: org }) => setOrgSelected(org))
          .catch(() => setOrgSelected(null))
          .finally(() => setResolvedOrgId(orgId));
      } else {
        setOrgSelected(loginInfo.organisation ?? null);
        setResolvedOrgId(orgId);
      }
    } else {
      setOrgSelected(null);
      setResolvedOrgId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);


  // The menu is derived from `orgSelected`, which resolves asynchronously after
  // `orgId` changes. Until it catches up the paths still reflect the PREVIOUS org
  // — report loading so SidebarBody waits instead of routing on stale paths
  // (which would bounce a freshly-set ?org= deep link to the default page).
  const orgNotReady = orgId != null && resolvedOrgId !== orgId;

  // orgScopedPaths is derived, not fetched, so it's merged in here rather than
  // stored in `data` — but the merge has to be memoised or every render hands
  // SidebarBody a new menu object and re-fires its effects (see above).
  return useMemo(
    () =>
      orgNotReady
        ? { ...data, orgScopedPaths, levelIndex, loading: true }
        : { ...data, orgScopedPaths, levelIndex },
    [data, orgScopedPaths, levelIndex, orgNotReady]
  );
};

export default useCrossLevelMenu;
